import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiSearch, FiTrash2, FiEdit2, FiX } from "react-icons/fi";
import csrLeadsService from "../../api/services/csrLeadsService";
import { TableSkeleton } from "../../components/common/skeletons";
import PaginationBar from "../../components/common/PaginationBar";
import {
  PHONE_DIAL_CODES,
  buildFullContactNo,
  clampNationalDigitsForDial,
  getNationalLengthLimits,
  parseContactForDialSelect,
} from "../../data/phoneDialCodes";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none w-full";

const ALLOWED = ["Admin", "Developer", "CSR", "CSR Team Lead"];
const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "registered", label: "Registered" },
  { value: "premium", label: "Premium" },
];

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function emptyForm() {
  return {
    fullName: "",
    email: "",
    agentId: "",
    notes: "",
  };
}

function StatusTag({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    registered: "bg-emerald-50 text-emerald-800 border-emerald-200",
    premium: "bg-indigo-50 text-indigo-800 border-indigo-200",
  };
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
        map[status] || map.pending
      }`}
    >
      {label}
    </span>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 min-w-[120px]">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || "text-gray-900"}`}>{value}</p>
    </div>
  );
}

function LeadModal({ mode, lead, agents, canPickAgent, onClose, onSaved }) {
  const initialPhone = mode === "edit" ? parseContactForDialSelect(lead.phone) : { dial: "+92", national: "" };
  const [form, setForm] = useState(() =>
    mode === "edit"
      ? {
          fullName: lead.fullName || "",
          email: lead.email || "",
          agentId: String(lead.agentId || ""),
          notes: lead.notes || "",
        }
      : {
          ...emptyForm(),
          agentId: agents[0]?.id ? String(agents[0].id) : "",
        }
  );
  const [phoneDial, setPhoneDial] = useState(initialPhone.dial);
  const [phoneNational, setPhoneNational] = useState(
    clampNationalDigitsForDial(initialPhone.dial, initialPhone.national)
  );
  const [saving, setSaving] = useState(false);

  const phoneLimits = useMemo(() => getNationalLengthLimits(phoneDial), [phoneDial]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleDialChange = (e) => {
    const nextDial = e.target.value;
    setPhoneDial(nextDial);
    setPhoneNational((prev) => clampNationalDigitsForDial(nextDial, prev));
  };

  const submit = async () => {
    if (!form.fullName.trim()) {
      toast.error("Name is required");
      return;
    }
    const national = phoneNational.replace(/\D/g, "");
    const fullPhone = national ? buildFullContactNo(phoneDial, national) : null;
    if (!form.email.trim() && !national) {
      toast.error("Email or phone is required");
      return;
    }
    if (national && (national.length < phoneLimits.min || national.length > phoneLimits.max)) {
      const range =
        phoneLimits.min === phoneLimits.max
          ? `exactly ${phoneLimits.min} digits`
          : `${phoneLimits.min}–${phoneLimits.max} digits`;
      toast.error(`Enter a valid phone number (${range} after the country code).`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim() || null,
        phone: fullPhone,
        notes: form.notes.trim(),
        ...(canPickAgent && form.agentId ? { agentId: Number(form.agentId) } : {}),
      };
      if (mode === "edit") {
        await csrLeadsService.updateLead(lead.id, payload);
        toast.success("Lead updated");
      } else {
        await csrLeadsService.createLead(payload);
        toast.success("Lead added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === "edit" ? "Edit lead" : "Add CSR lead"}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <FiX className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Full name *</label>
            <input
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              className={inputCls}
              placeholder="Customer name"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Email</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className={inputCls}
              placeholder="email@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Contact (country code + number)</label>
            <div className="flex gap-2 flex-wrap sm:flex-nowrap">
              <select
                value={phoneDial}
                onChange={handleDialChange}
                className={`${inputCls} sm:max-w-[200px] shrink-0`}
              >
                {PHONE_DIAL_CODES.map((pc) => (
                  <option key={pc.code} value={pc.code}>
                    {pc.code} ({pc.country})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="Phone number (digits only)"
                value={phoneNational}
                maxLength={phoneLimits.max}
                onChange={(e) =>
                  setPhoneNational(clampNationalDigitsForDial(phoneDial, e.target.value))
                }
                className={`${inputCls} flex-1 min-w-0`}
              />
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              {phoneLimits.min === phoneLimits.max
                ? `Exactly ${phoneLimits.max} digits after the code.`
                : `${phoneLimits.min}–${phoneLimits.max} digits after the code.`}
            </p>
          </div>
          {canPickAgent && (
            <div>
              <label className="text-xs font-medium text-gray-600">Agent</label>
              <select name="agentId" value={form.agentId} onChange={handleChange} className={inputCls}>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name || a.email}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-600">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              className={inputCls}
              rows={3}
              placeholder="Optional notes from first contact"
            />
          </div>
          <p className="text-xs text-gray-500">Provide at least email or phone so we can match on register.</p>
        </div>
        <div className="px-5 py-4 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={submit}
            className="px-4 py-2 text-sm rounded-lg text-white font-medium disabled:opacity-60"
            style={{ backgroundColor: THEME.primary }}
          >
            {saving ? "Saving…" : mode === "edit" ? "Save" : "Add lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CsrLeadsPage() {
  const role = localStorage.getItem("adminUserRole") || "Admin";
  const canPickAgent = ["Admin", "Developer", "CSR Team Lead"].includes(role);
  const canDeleteLead = canPickAgent;
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ pending: 0, registered: 0, premium: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accessRes, leadsRes, statsRes] = await Promise.all([
        csrLeadsService.getAccess(),
        csrLeadsService.listLeads({
          search: debouncedSearch,
          page,
          limit: PAGE_SIZE,
          ...(statusFilter !== "all" ? { status: statusFilter } : {}),
          ...(agentFilter !== "all" ? { agent_id: agentFilter } : {}),
        }),
        csrLeadsService.getStats({
          ...(agentFilter !== "all" ? { agent_id: agentFilter } : {}),
        }),
      ]);
      setAgents(accessRes.access?.agents || []);
      setLeads(leadsRes.leads || []);
      setTotal(leadsRes.total ?? 0);
      setTotalPages(leadsRes.totalPages ?? 1);
      setStats(statsRes.stats || { pending: 0, registered: 0, premium: 0, total: 0 });
    } catch {
      toast.error("Failed to load CSR leads");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, agentFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, agentFilter]);

  if (!ALLOWED.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this lead?")) return;
    try {
      await csrLeadsService.deleteLead(id);
      toast.success("Lead removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to remove lead");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CSR Leads</h1>
          <p className="text-sm text-gray-500 mt-1">
            Log name, email, and phone before signup. When the user registers or buys premium, the lead is tagged and counted for the agent.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: "add" })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
          style={{ backgroundColor: THEME.primary }}
        >
          <FiPlus className="h-4 w-4" />
          Add lead
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <StatCard label="Pending" value={stats.pending} accent="text-amber-700" />
        <StatCard label="Registered" value={stats.registered} accent="text-emerald-700" />
        <StatCard label="Premium" value={stats.premium} accent="text-indigo-700" />
        <StatCard label="Total" value={stats.total} />
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, agent…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`${inputCls} md:w-44`}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {canPickAgent && (
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className={`${inputCls} md:w-48`}
          >
            <option value="all">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || a.email}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-4">
            <TableSkeleton rows={8} cols={7} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Agent</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                      No leads yet. Add a lead after first contact.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                      <td className="px-4 py-3 font-medium text-gray-900">{lead.fullName}</td>
                      <td className="px-4 py-3 text-gray-700">{lead.email || "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{lead.phone || "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{lead.agentName || "—"}</td>
                      <td className="px-4 py-3">
                        <StatusTag status={lead.status} />
                        {lead.linkedUserId ? (
                          <p className="text-[11px] text-gray-400 mt-1">User #{lead.linkedUserId}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {lead.createdAt ? new Date(lead.createdAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {lead.status === "pending" && (
                            <button
                              type="button"
                              title="Edit"
                              onClick={() => setModal({ mode: "edit", lead })}
                              className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                            >
                              <FiEdit2 className="h-4 w-4" />
                            </button>
                          )}
                          {canDeleteLead && (
                            <button
                              type="button"
                              title="Delete"
                              onClick={() => handleDelete(lead.id)}
                              className="p-1.5 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && totalPages > 1 && (
          <div className="border-t px-4 py-3">
            <PaginationBar
              page={page}
              totalPages={totalPages}
              totalItems={total}
              pageSize={PAGE_SIZE}
              label="leads"
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {modal && (
        <LeadModal
          mode={modal.mode}
          lead={modal.lead}
          agents={agents}
          canPickAgent={canPickAgent}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
